import { ChangeEvent, useMemo } from 'react';
import { Box, Card, Title } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { InterfaceNameValue } from '@tiendanube/components';
import { InterfaceNameBooleanValue } from 'commons/types';
import { getTodayIsoString, subtractDays } from 'commons/utils/date';
import { PersonalDataState } from 'domains/PagoNubeOnboarding/Onboarding/types';
import { personalDataElements } from 'domains/PagoNubeOnboarding/Onboarding/utils';
import StoreCategory from 'domains/PagoNubeOnboarding/StoreCategory/components';
import useTranslationPagoNubeOnboarding from 'domains/PagoNubeOnboarding/useTranslationPagoNubeOnboarding';
import { PhoneNumberData } from './PhoneNumberData';

interface PersonalDataProps {
  errors: Partial<Record<string, string>>;
  handleOnBlur: (data: InterfaceNameValue) => void;
  onChange: (data: InterfaceNameValue | InterfaceNameBooleanValue) => void;
  personalDataState: PersonalDataState;
}

const minDate = '1910-01-01';
const maxDate = subtractDays(getTodayIsoString(), 4748); // 13 years

function PersonalData({
  errors,
  handleOnBlur,
  onChange,
  personalDataState,
}: Readonly<PersonalDataProps>) {
  const t = useTranslationPagoNubeOnboarding('personalData');

  const formFields = useMemo(
    () =>
      personalDataElements.map((element) => {
        const key = element.key;
        const isDate = element.type === 'date';
        return key === 'phoneNumber' ? (
          <PhoneNumberData
            key={key}
            element={element}
            errors={errors}
            handleOnBlur={handleOnBlur}
            onChange={onChange}
            personalDataState={personalDataState}
          />
        ) : (
          <FormField.Input
            key={key}
            appearance={errors[key] ? 'danger' : 'none'}
            label={t(key)}
            type={element.type}
            onBlur={(e) => handleOnBlur({ name: key, value: e.target.value })}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange({ name: key, value: e.target.value })
            }
            property={key}
            value={personalDataState[key] || ''}
            min={isDate ? minDate : undefined}
            max={isDate ? maxDate : undefined}
            showHelpText={!!errors[key]}
            helpText={errors[key]}
            placeholder={t(`${key}Placeholder`)}
          />
        );
      }),
    [errors, personalDataState, handleOnBlur, onChange, t],
  );

  return (
    <Box marginBottom="4">
      <Card>
        <Title as="h3">{t('title')}</Title>
        {formFields}
        <StoreCategory
          errors={errors}
          onChange={onChange}
          personalDataState={personalDataState}
        />
      </Card>
    </Box>
  );
}

export default PersonalData;
